<template>
	<div class="map" ref="map" v-on:click="resetAll()" v-on:mousedown="startDrag" v-on:mouseup="endDrag" v-on:mouseleave="endDrag" v-on:mousemove="onDrag" v-on:mousewheel="onWheel" v-on:DOMMouseScroll="onWheel">
		<inline-svg 
			id="svgMap" 
			:src="mapImage" 
			@loaded="svgLoaded($event)" 
			:class="[
				{ 'garden': this.currentMap === 'gardens' },
				{ 'interior': this.currentMap === 'main' },
				{ 'lobby': this.screen === 'lobby' },
				{ 'stairwell': this.screen === 'stairwell' },
				{ 'selected': this.mapSelected === true },
				{ 'highlightStart': this.highlightStartPoint === true },
				{ 'interior-visible': this.interiorZoomThreshold === true },
				{ 'outdoor-path-hidden': this.outdoorPath !== true },
				{ 'map-visible': this.mapVisible === true },
				{ 'emergency-exits-visible': this.emergencyExitsVisible === true }
			]">
		</inline-svg>
		<MapLabels :zoomFactor="this.zoomFactor" :mapLoaded="this.mapLoaded" :lastSelected="this.lastPrefix" @selectLabel="onLabelSelected" />
	</div>
</template>

<script>
	import InlineSvg from 'vue-inline-svg';
	import Vivus from 'vivus';
	import PinchZoom from 'pinch-zoom-js';
	import normalizeWheel from 'normalize-wheel';
	import { gsap, Power1 } from 'gsap';
	import positionData from '../data/MapPositions.json'
	import MapLabels from '@/components/MapLabels.vue';

	export default {
		name: "Map",
		components: {
			InlineSvg,
			MapLabels
		},
		props: {
			
		},
		data: function() {
			return {
				mapImage: require('../assets/map/ahc_map_combined.svg'),
				sharedStore: window.store,
				debugSvg: false,
				vivusList: {},
				roomElements: {},
				mapSelected: false,
				mapVisible: false,
				mapLoaded: false,
				mapIcons: [],
				highlightStartPoint: true,
				refLength: 1500,
				lastPrefix: "",
				dragging: false,
				dragStartPos: {
					x: 0,
					y: 0
				},
				origin: 0,
				zoomLib: null,
				zoomFactor: 1,
				mapControlsInitialized: false,
				timeline: new gsap.timeline(),
				roomPrefixes: [
					"FP",
					"ST",
					"GOB",
					"NL",
					"KT",
					"Ga",
					"NG",
					"TP",
					"SJ",
					"BC",
					"GS",
					"OG",
					"Lo",
					"Co",
					"Cy",
					"TK",
					"ACC",
					"RVV"
				],
				gardenPrefixes: [
					"PD",
					"VEP",
					"LP",
					"SF",
					"RG",
					"AG",
					"WC",
					"SCHR",
					"SW",
					"SH",
					"SL",
					"VP",
					"QG",
					"OGG",
					"SG",
					"EG"
				],
				mcelreathPrefixes: [
					"MH"
				]
			}
		},
		mounted: function() {
			
		},
		created() {
			window.addEventListener("resize", this.onResize);
		},
		destroyed() {
			window.removeEventListener("resize", this.onResize);
		},
		computed: {
			screen: function() {
				return this.sharedStore.state.screen;
			},
			currentMap: function() {
				return this.sharedStore.state.currentMap;
			},
			currentLocation: function() {
				return this.sharedStore.state.currentLocation;
			},
			outdoorPath: function() {
				return this.sharedStore.state.showOutdoorPath;
			},
			positionList: function() {
				if (this.currentMap === "main") {
					return positionData.interior;
				} else if (this.currentMap === "gardens") {
					return positionData.exterior;
				} else {
					return positionData.mcelreath;
				}
			},
			interiorZoomThreshold: function() {
				if (this.zoomLib && this.zoomLib.zoomFactor >= 3.5) {
					return true;
				} else {
					return false;
				}
			},
			emergencyExitsVisible: function() {
				return this.sharedStore.state.emergencyExitsVisible;
			}
		},
		watch: {
			"sharedStore.state.directorySelection": function(e) {
				if (e === "") {
					return;
				}

				// set current map based on selection
				if (this.roomPrefixes.includes(e)) {
					this.sharedStore.setCurrentMap("main");
				} else if (this.gardenPrefixes.includes(e)) {
					this.sharedStore.setCurrentMap("gardens");
				} else if (this.mcelreathPrefixes.includes(e)) {
					this.sharedStore.setCurrentMap("mcelreath");
				}

				this.$nextTick(() => {
					this.focusMapAndSelect(e);
					this.sharedStore.setDirectorySelection("");
				});
			},
			"sharedStore.state.currentLocation": function(e) {
				if (e === "") {
					// if current location is reset somewhere else, reset here as well
					this.resetAll();
				}
			},
			"sharedStore.state.currentMap": function(e) {
				// if the current map changes, and there is no current location, refocus the map
				if (this.currentLocation !== "") {
					// do nothing, map will focus on the selected location
				} else if (e === "main") {
					this.focusMap(positionData.defaults.interior, null);
				} else if (e === "gardens") {
					this.focusMap(positionData.defaults.exterior, null);
				} else if (e === "mcelreath") {
					this.focusMap(positionData.defaults.mcelreath, null);
				}
			},
			"sharedStore.state.lastZoomUpdate": function() {
				let newZoom = this.zoomLib.zoomFactor + this.sharedStore.state.mapZoomDelta;
				let center = {
					x: this.$refs["map"].offsetWidth * 0.25,
					y: this.$refs["map"].offsetHeight
				};
				
				this.zoomLib.scaleTo(newZoom, center);
				this.zoomLib.update();
			}
		},
		methods: {
			svgLoaded: function(svgEl) {
				if (this.debugSvg) {
					console.log("svg loaded");
				}
				
				// set up Vivus - this will throw errors in dev when hot reloading
				this.setupMap(svgEl);
				this.setupMapControls();
				setTimeout(() => {
					this.recalculateMinZoom();
				}, 100);

				this.mapLoaded = true;
			},
			setupMap: function(svgEl) {
				this.setupVivusPaths(svgEl, "interior");
				this.setupVivusPaths(svgEl, "exterior");
				this.setupVivusPaths(svgEl, "mcelreath");

				let mainBuildingOverlay = document.getElementById("MainBldgMap_Overlay");
				if (mainBuildingOverlay) {
					this.mainBuildingOverlay = mainBuildingOverlay;
				} else {
					this.mainBuildingOverlay = null;
				}

				// set 'you are here' origin
				if (this.screen === "lobby") {
					this.origin = 1;
				} else if (this.screen === "stairwell") {
					this.origin = 2;
				} else {
					// no path drawing
				}

				// set click handler on main building click area
				let mainBuildingHitArea = document.getElementById("MainBuilding_Click");
				if (mainBuildingHitArea) {
					mainBuildingHitArea.addEventListener("click", () => {
						this.sharedStore.setCurrentLocation("");
						this.sharedStore.setCurrentMap("main");
						this.focusMap(positionData.defaults.interior, null);
					});
				}

				// push icons into array to set scale
				this.mapIcons.push(document.getElementById("PicnicTable_01"));
				this.mapIcons.push(document.getElementById("PicnicTable_02"));
				this.mapIcons.push(document.getElementById("Restroom_01"));
				this.mapIcons.push(document.getElementById("Accessibility_01"));
				this.mapIcons.push(document.getElementById("Accessibility_02"));
			},
			setupVivusPaths: function(svgEl, mapView) {

				let prefixList = [];
				if (mapView === "interior") {
					prefixList = this.roomPrefixes;
				} else if (mapView === "exterior") {
					prefixList = this.gardenPrefixes;
				} else {
					prefixList = this.mcelreathPrefixes;
				}

				for (let i = 0; i < prefixList.length; i++) {
					let prefix = prefixList[i];

					this.roomElements[prefix] = {};
					let roomObj = this.roomElements[prefix];

					roomObj.mapView = mapView;

					let pathId = prefix + "_Path1";
					let pathId2 = prefix + "_Path2";
					let capId = prefix + "_Path_Cap";
					let hitAreaId = prefix + "_Click";
					let outlineId = prefix + "_Outline";

					roomObj.path = new Vivus(svgEl,  { 
						duration: 200, 
						pathId: pathId,
						start: "manual",
						type: "sync"
					}, () => this.onPathComplete(prefix));

					roomObj.path2 = new Vivus(svgEl,  { 
						duration: 200, 
						pathId: pathId2,
						start: "manual",
						type: "sync"
					}, () => this.onPathComplete(prefix));

					roomObj.cap = document.getElementById(capId);
					if (roomObj.cap) {
						roomObj.cap.style.opacity = 0;
					} else {
						if (this.debugSvg) {
							console.log("No cap for " + prefix);
						}
					}
					
					roomObj.hitArea = document.getElementById(hitAreaId);
					if (roomObj.hitArea) {
						roomObj.hitArea.style.opacity = 0;
						roomObj.hitArea.addEventListener("click", (e) => {
							this.selectRoom(e, prefix);
						});
					} else {
						if (this.debugSvg) {
							console.log("No hit area for " + prefix);
						}
					}

					roomObj.outline = document.getElementById(outlineId);
					if (roomObj.outline) {
						roomObj.outline.style.opacity = 0;
					} else {
						if (this.debugSvg) {
							console.log("No outline for " + prefix);
						}
					}
				}
			},
			focusMapAndSelect: function(prefix) {
				// when selecting from directory, automatically change map zoom and focus before bringing up location
				this.mapSelected = true;
				this.highlightStartPoint = false;

				// if we have a position listed, pan the map here - otherwise skip directly to info panel
				if (!this.positionList || !this.positionList[prefix]) {
					this.selectRoom(null, prefix);
					return;
				}

				// get percentages for target offsets
				let target = this.positionList[prefix];

				this.focusMap(target, prefix);
			},
			focusMap: function(target, prefix) {
				// set var for current map position
				let from = {
					x: this.zoomLib.offset.x,
					y: this.zoomLib.offset.y,
					z: this.zoomLib.zoomFactor
				};

				this.zoomLib.scaleTo(target.z, {x: 0, y: 0});
				this.zoomLib.update();

				// get maximum offsets - requires setting zoom to get max offsets
				let max = this.getMaxOffset();

				// reset to original zoom and position
				this.zoomLib.scaleTo(from.z, {x: 0, y: 0});
				this.zoomLib.offset.x = from.x;
				this.zoomLib.offset.y = from.y;
				this.zoomLib.update();

				// calculate into pixel positions
				let to = {
					x: max.x * target.x,
					y: max.y * target.y,
					z: target.z
				};

				// if the map is already in this position, select immediately
				if (prefix && 
					Math.abs(from.x - to.x) < 20 &&
					Math.abs(from.y - to.y) < 20 &&
					Math.abs(from.z - to.z) < 0.05 
				) {
					this.selectRoom(null, prefix);
					return;
				}

				// make timeline to animate
				this.timeline.kill();
				this.timeline = new gsap.timeline();

				this.timeline.to(from, {
					duration: 1,
					x: to.x,
					y: to.y, 
					z: to.z,
					ease: Power1.easeInOut,
					onUpdate: () => {
						
						this.zoomLib.scaleTo(from.z, {
							x: from.x,
							y: from.y
						});
						this.zoomLib.update();
						this.zoomLib.offset.x = from.x;
						this.zoomLib.offset.y = from.y;
						this.zoomLib.update();
					}
				}, 0.5);

				if (prefix) {
					this.timeline.call(() => {
						this.selectRoom(null, prefix);
					}, null);
				}
			},
			selectRoom: function(e, prefix) {
				this.$emit("hideEvents");
				if (e !== null) {
					e.stopPropagation();

					let endPos = {
						x: e.x,
						y: e.y
					};

					if (Math.abs(this.dragStartPos.x - endPos.x) > 25 || Math.abs(this.dragStartPos.y - endPos.y) > 25) {
						console.log("Ignoring click handler after drag");
						return;
					}
				}

				// Find the actual location information - if location is hidden, ignore selection
				if (prefix) {
					let allData = this.sharedStore.state.locationData || {};
					let selectedData = allData.filter(obj => {
						return obj.code === prefix;
					});
					if (selectedData.length && selectedData[0].hidden === true) {
						return;
					}
				}

				if (this.dragging === true) {
					// don't interpret clicks if we are panning the map
					return;
				}
				
				this.resetAll();

				this.mapSelected = true;
				this.highlightStartPoint = false;

				// save lat prefix, communicate to labels
				this.lastPrefix = prefix;

				let pathRef = "";
				if (this.origin === 0) {
					// ignore path drawing for non-kiosk implementations
					this.timeline.kill();
					this.timeline = new gsap.timeline();
					this.timeline.call(() => {
						this.sharedStore.setCurrentLocation(prefix);
						this.lastPrefix = "";
					}, null, "+=0.5");
					return;
				} else if (this.origin === 1) {
					pathRef = "path"
				} else if (this.origin === 2) {
					pathRef = "path2"
				}

				let vivusRef = this.roomElements[prefix][pathRef];
				// get the ratio of how long this path is to a known constant
				let pathLength = vivusRef.map[0].length;
				let ratio = pathLength / this.refLength;
				// play animation, adjusting so that speed remains constant between all paths
				vivusRef.play(1 / ratio);
			},
			onLabelSelected: function(e, prefix) {
				// label selected directly
				this.selectRoom(e, prefix);
			},
			resetAll: function() {
				for (var key in this.roomElements) {
					this.roomElements[key].path.stop();
					this.roomElements[key].path.reset();

					this.roomElements[key].path2.stop();
					this.roomElements[key].path2.reset();

					if (this.roomElements[key].outline) {
						this.roomElements[key].outline.style.opacity = 0;
					}

					if (this.roomElements[key].cap) {
						this.roomElements[key].cap.style.opacity = 0;
					}
				}

				this.mapSelected = false;

				this.sharedStore.setCurrentLocation("");
			},
			onPathComplete: function(prefix) {
				this.sharedStore.setCurrentLocation(prefix);
				this.lastPrefix = "";

				if (this.roomElements[prefix].cap) {
					this.roomElements[prefix].cap.style.opacity = 1;
				}
			},
			setupMapControls: function() {
				if (this.mapControlsInitialized === true) {
					return;
				}
				this.mapControlsInitialized = true;
				this.zoomLib = new PinchZoom(document.querySelector(".map"), {
					minZoom: 1,
					maxZoom: 8,
					verticalPadding: 0,
					onZoomUpdate: this.onZoomUpdate
				});

				this.zoomLib.update();
				
			},
			startDrag: function(e) {
				this.zoomLib.handleDragStart({
					touches: [e]
				});
				this.zoomLib.update();
				this.dragging = true;

				this.dragStartPos = {
					x: e.x,
					y: e.y
				}
			},
			onDrag: function(e) {
				if (this.dragging === true) {
					this.zoomLib.handleDrag({
						touches: [e]
					});
					this.zoomLib.update();
				}
			},
			endDrag: function() {
				// not calling end drag, as it appears to cause the map container to re-mount
				this.zoomLib.handleDragEnd();
				this.zoomLib.update();
				this.dragging = false;

				// let max = this.getMaxOffset();
				// console.log("x: ", this.zoomLib.offset.x / max.x, "y: ", this.zoomLib.offset.y / max.y, "z: ", this.zoomLib.zoomFactor);
			},
			onWheel: function(e) {
				// mousewheel zoom support for desktop browsers
				let eNormalized = normalizeWheel(e);

				let newZoom = this.zoomLib.zoomFactor + (-0.1 * eNormalized.spinY);
				let center = {
					x: e.x, 
					y: e.y
				};
				if (eNormalized.spinY > 0) {
					center = {
						x: this.$refs["map"].offsetWidth / 2,
						y: this.$refs["map"].offsetHeight / 2
					};
				}
				this.zoomLib.scaleTo(newZoom, center);
				this.zoomLib.update();

				// let max = this.getMaxOffset();
				// console.log("x: ", this.zoomLib.offset.x / max.x, "y: ", this.zoomLib.offset.y / max.y, "z: ", this.zoomLib.zoomFactor);

				// update position so that we don't zoom out of bounds
				let adjusted = this.zoomLib.sanitizeOffset({
					x: this.zoomLib.offset.x, 
					y: this.zoomLib.offset.y
				});
				this.zoomLib.offset.x = adjusted.x;
				this.zoomLib.offset.y = adjusted.y;
				this.zoomLib.update();
			},
			onZoomUpdate: function(e) {
				this.zoomFactor = e.zoomFactor;

				// update position so that we don't zoom out of bounds
				let adjusted = this.zoomLib.sanitizeOffset({
					x: this.zoomLib.offset.x, 
					y: this.zoomLib.offset.y
				});
				this.zoomLib.offset.x = adjusted.x;
				this.zoomLib.offset.y = adjusted.y;
				this.zoomLib.update();

				// scale icons
				this.mapIcons.forEach((icon) => {
					let newScale = Math.min(2 / this.zoomFactor, 1);
					newScale = Math.max(newScale, 0.5);
					if (icon) {
						gsap.set(icon, {
							scale: newScale
						});
					}
				});
			},
			onResize: function() {
				// account for delays in updated screen size reporting
				setTimeout(() => {
					this.recalculateMinZoom();
				}, 0);
			},
			recalculateMinZoom: function() {
				let width = this.zoomLib.container.offsetWidth;
				let height = this.zoomLib.container.offsetHeight;
				let screenAspect = width / height;
				let mapAspect = this.$refs["map"].offsetWidth / this.$refs["map"].offsetHeight;
				let minZoom = 1;

				if (screenAspect === Infinity) {
					console.log("infinite aspect ratio - wait and try again")
					setTimeout(() => {
						this.recalculateMinZoom();
					}, 100);
					return;
				}

				if (screenAspect < mapAspect) {
					// container is narrower thank our map aspect - fit height and expand width
					minZoom = this.zoomLib.container.offsetHeight / this.$refs["map"].offsetHeight;
				} else {
					// container is wider than our map aspect - fit width and expand height
					// we need to recalculate map width since it is always 100% wide
					minZoom = (this.zoomLib.container.offsetWidth / mapAspect) / this.zoomLib.container.offsetHeight;
				}

				// set scale to minimum zoom
				this.zoomLib.options.minZoom = minZoom;
				this.zoomLib.scaleTo(minZoom, {x: 0, y: 0});
				this.zoomLib.update();

				// after adjusting zoom, reposition
				let max = this.getMaxOffset();
				this.zoomLib.offset.x = max.x / 2;
				this.zoomLib.offset.y = max.y;
				this.zoomLib.update();

				// we have recalculated any map position issues - fade it in
				this.mapVisible = true;
			},
			getMaxOffset: function() {
				let zoomWidth = this.zoomLib.el.offsetWidth * this.zoomLib.getInitialZoomFactor() * this.zoomLib.zoomFactor;
				let zoomHeight = this.zoomLib.el.offsetHeight * this.zoomLib.getInitialZoomFactor() * this.zoomLib.zoomFactor;
				let maxX = zoomWidth - this.zoomLib.getContainerX() + this.zoomLib.options.horizontalPadding;
				let maxY = zoomHeight - this.zoomLib.getContainerY() + this.zoomLib.options.verticalPadding;

				return {
					x: maxX,
					y: maxY
				};
			}
		}
	};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
	.map {
		position: relative;
		width: 100%;
		overflow: hidden;
		margin-bottom: 50px;
		background: rgb(14, 121, 97);
	}

	.map >img {
		position: absolute;
		top: -2px;
		left: 0;
		width: 100%;
		min-width: 100%;
		min-height: 100%;
		object-fit: contain;
	}

	svg {
		position: relative;
		min-width: 100%;
		min-height: 100%;
	}

	@media (min-aspect-ratio: 1/1) {
		.map {
			padding-bottom: 0;
		}
	}

	#svgMap {
		transition: opacity 0.5s;
		opacity: 0;

		&.map-visible {
			opacity: 1;
		}
	}
</style>

<style lang="scss">

	@media (max-aspect-ratio: 1/1) {
		// mobile view
		.pinch-zoom-container {
			margin-bottom: 47px;
			height: calc(100% - 31px)!important;
		}
	}

	@media (min-aspect-ratio: 1/1) {
		// desktop two-column view
		.pinch-zoom-container, .map {
			width: 50%;
		}
	}

	#MainBldgMap_Overlay {
		transition: 0.2s;
		opacity: 0;
	}

	#YouAreHere_Interior_Position1,
	#YouAreHere_Interior_Position2,
	#YouAreHere_Exterior_Position1,
	#YouAreHere_Exterior_Position2 {
		transition: 0.2s;
		opacity: 0;
	}

	.kiosk {
		// only reveal interior map layout when we have a path to draw
		.selected #MainBldgMap_Overlay {
			opacity: 1;
		}

		.lobby {
			#YouAreHere_Exterior_Position1 {
				opacity: 1;
			}
		}

		.stairwell {
			#YouAreHere_Exterior_Position2 {
				opacity: 1;
			}
		}
	}

	#MainBuilding_Click {
		opacity: 0;
	}

	#Main_Building_Diagram {
		pointer-events: none;
		transition: 0.4s;
		opacity: 0;
	}

	.interior-visible, .kiosk {
		// interior diagram always visible in kiosk mode
		#Main_Building_Diagram {
			opacity: 1;
		}

		#MainBuilding_Click {
			pointer-events: none;
		}
	}

	// emergency exit icon visibility
	#Wide_Map_Exits_Red, #Main_Building_Exits_Red, #McElreath_Hall_Exits_Red {
		pointer-events: none;
		transition: 0.4s;
		opacity: 0;
	}

	.emergency-exits-visible {
		#Wide_Map_Exits_Red, #Main_Building_Exits_Red, #McElreath_Hall_Exits_Red {
			opacity: 1;
		}
	}
</style>